import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import { 
  User, 
  MapPin, 
  Phone, 
  Mail,
  CreditCard,
  Package,
  Clock,
  TrendingUp,
  TrendingDown,
  Loader2,
  Award,
  Star,
  Trophy,
  Gift,
  Leaf,
  Shield,
  Crown,
  UserPlus
} from "lucide-react";

const badgeIcons = {
  "user-plus": UserPlus,
  "gift": Gift,
  "leaf": Leaf,
  "trophy": Trophy,
  "shield": Shield,
  "star": Star,
  "award": Award,
  "crown": Crown
};

const ProfilePage = () => {
  const { user, refreshUser } = useAuth();
  const [myProducts, setMyProducts] = useState([]);
  const [pointsHistory, setPointsHistory] = useState([]);
  const [badgesData, setBadgesData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, pointsRes, badgesRes] = await Promise.all([
        axios.get(`${API}/my-products`, { withCredentials: true }),
        axios.get(`${API}/points-history`, { withCredentials: true }),
        axios.get(`${API}/my-badges`, { withCredentials: true })
      ]);
      setMyProducts(productsRes.data);
      setPointsHistory(pointsRes.data);
      setBadgesData(badgesRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const statusLabels = {
    pending: { label: "Pendiente", color: "bg-yellow-100 text-yellow-700" },
    listed: { label: "Publicado", color: "bg-green-100 text-green-700" },
    reserved: { label: "Reservado", color: "bg-blue-100 text-blue-700" },
    delivered: { label: "Entregado", color: "bg-slate-100 text-slate-700" },
    rejected: { label: "Rechazado", color: "bg-red-100 text-red-700" }
  };

  const BadgeIcon = ({ iconName }) => {
    const Icon = badgeIcons[iconName] || Award;
    return <Icon className="w-6 h-6" />;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#374B67]" />
        </div>
      </Layout>
    );
  }

  const nextBadgeProgress = () => {
    const donated = badgesData?.progress?.products_donated || 0;
    const milestones = [1, 3, 5, 10, 20];
    const nextMilestone = milestones.find(m => m > donated) || milestones[milestones.length - 1];
    const prevMilestone = milestones.filter(m => m <= donated).pop() || 0;
    const progress = ((donated - prevMilestone) / (nextMilestone - prevMilestone)) * 100;
    return { progress: Math.min(progress, 100), next: nextMilestone, current: donated };
  };

  const badgeProgress = nextBadgeProgress();

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold text-[#374B67] mb-8" data-testid="profile-title">
          Mi Perfil
        </h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* User Info Card */}
          <div className="lg:col-span-1">
            <Card className="border-0 shadow-lg sticky top-24">
              <CardContent className="p-6">
                {/* Avatar */}
                <div className="text-center mb-6">
                  {user?.picture ? (
                    <img 
                      src={user.picture} 
                      alt={user.name} 
                      className="w-24 h-24 rounded-full mx-auto border-4 border-[#69C7EA]/30"
                    />
                  ) : (
                    <div className="w-24 h-24 bg-[#374B67] rounded-full flex items-center justify-center mx-auto text-white text-3xl font-bold">
                      {user?.name?.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <h2 className="text-xl font-bold text-[#374B67] mt-4">{user?.name}</h2>
                </div>

                {/* Points */}
                <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-4 text-center mb-6">
                  <p className="text-sm text-green-600">Mis Puntos</p>
                  <p className="text-4xl font-bold text-green-700">{user?.points || 0}</p>
                </div>

                {/* Badges Preview */}
                {badgesData && badgesData.earned_badges.length > 0 && (
                  <div className="mb-6">
                    <p className="text-sm text-slate-500 mb-2">Mis Insignias ({badgesData.earned_badges.length})</p>
                    <div className="flex flex-wrap gap-2">
                      {badgesData.earned_badges.slice(0, 4).map((badge) => (
                        <div 
                          key={badge.id} 
                          className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center"
                          title={badge.name}
                        >
                          <BadgeIcon iconName={badge.icon} />
                        </div>
                      ))}
                      {badgesData.earned_badges.length > 4 && (
                        <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center text-slate-500 text-sm font-bold">
                          +{badgesData.earned_badges.length - 4}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* User Details */}
                <div className="space-y-4">
                  <div className="flex items-center gap-3 text-slate-600">
                    <Mail className="w-5 h-5 text-slate-400" />
                    <span className="text-sm truncate">{user?.email}</span>
                  </div>
                  {user?.dni && (
                    <div className="flex items-center gap-3 text-slate-600">
                      <CreditCard className="w-5 h-5 text-slate-400" />
                      <span className="text-sm">{user.dni}</span>
                    </div>
                  )}
                  {user?.address && (
                    <div className="flex items-center gap-3 text-slate-600">
                      <MapPin className="w-5 h-5 text-slate-400" />
                      <span className="text-sm">{user.address}, {user.postal_code}</span>
                    </div>
                  )}
                  {user?.phone && (
                    <div className="flex items-center gap-3 text-slate-600">
                      <Phone className="w-5 h-5 text-slate-400" />
                      <span className="text-sm">{user.phone}</span>
                    </div>
                  )}
                </div>

                {user?.is_admin && (
                  <Badge className="w-full justify-center mt-6 bg-[#374B67]">
                    Administrador
                  </Badge>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Content Area */}
          <div className="lg:col-span-2">
            <Tabs defaultValue="badges">
              <TabsList className="w-full justify-start mb-6">
                <TabsTrigger value="badges" data-testid="tab-badges">Insignias</TabsTrigger>
                <TabsTrigger value="products" data-testid="tab-products">Mis Productos</TabsTrigger>
                <TabsTrigger value="points" data-testid="tab-points">Historial de Puntos</TabsTrigger>
              </TabsList>

              {/* Badges Tab */}
              <TabsContent value="badges">
                {/* Progress Card */}
                <Card className="border-0 shadow-md mb-6 bg-gradient-to-r from-amber-50 to-yellow-50">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center">
                        <Trophy className="w-7 h-7 text-amber-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-[#374B67]">Progreso hacia la siguiente insignia</h3>
                        <p className="text-sm text-slate-500">
                          {badgeProgress.current} de {badgeProgress.next} productos donados
                        </p>
                      </div>
                    </div>
                    <Progress value={badgeProgress.progress} className="h-3" />
                  </CardContent>
                </Card>

                {/* All Badges Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {badgesData?.earned_badges.map((badge) => (
                    <Card key={badge.id} className="border-0 shadow-sm bg-gradient-to-br from-amber-50 to-yellow-50">
                      <CardContent className="p-4 text-center">
                        <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                          <BadgeIcon iconName={badge.icon} />
                        </div>
                        <h4 className="font-semibold text-[#374B67] text-sm">{badge.name}</h4>
                        <p className="text-xs text-slate-500 mt-1">{badge.description}</p>
                        <Badge className="mt-2 bg-green-100 text-green-700 text-xs">Conseguida</Badge>
                      </CardContent>
                    </Card>
                  ))}
                  
                  {/* Locked badges placeholder */}
                  {badgesData && Array.from({ length: Math.max(0, 8 - badgesData.earned_badges.length) }).map((_, i) => (
                    <Card key={`locked-${i}`} className="border-0 shadow-sm bg-slate-50 opacity-50">
                      <CardContent className="p-4 text-center">
                        <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center mx-auto mb-3">
                          <Award className="w-8 h-8 text-slate-400" />
                        </div>
                        <h4 className="font-semibold text-slate-400 text-sm">???</h4>
                        <p className="text-xs text-slate-400 mt-1">Sigue participando para desbloquear</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>

              {/* My Products Tab */}
              <TabsContent value="products">
                {myProducts.length === 0 ? (
                  <Card className="border-dashed">
                    <CardContent className="p-8 text-center">
                      <Package className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                      <h3 className="text-lg font-semibold text-slate-600 mb-2">No has añadido productos</h3>
                      <p className="text-slate-500">Añade tu primer producto y gana 100 puntos</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {myProducts.map((product) => (
                      <Card key={product.product_id} className="overflow-hidden">
                        <div className="flex">
                          <div className="w-24 h-24 flex-shrink-0">
                            <img
                              src={product.image_url || "https://via.placeholder.com/100"}
                              alt={product.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <CardContent className="p-4 flex-1">
                            <div className="flex items-start justify-between">
                              <div>
                                <h3 className="font-semibold text-[#374B67]">{product.name}</h3>
                                <p className="text-sm text-slate-500 line-clamp-1">{product.description}</p>
                              </div>
                              <Badge className={statusLabels[product.status]?.color}>
                                {statusLabels[product.status]?.label}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {new Date(product.created_at).toLocaleDateString("es-ES")}
                              </span>
                            </div>
                          </CardContent>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Points History Tab */}
              <TabsContent value="points">
                {pointsHistory.length === 0 ? (
                  <Card className="border-dashed">
                    <CardContent className="p-8 text-center">
                      <TrendingUp className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                      <h3 className="text-lg font-semibold text-slate-600 mb-2">Sin movimientos</h3>
                      <p className="text-slate-500">Aquí verás tu historial de puntos</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {pointsHistory.map((tx) => (
                      <Card key={tx.transaction_id} className="overflow-hidden">
                        <CardContent className="p-4 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${tx.amount > 0 ? "bg-green-100" : "bg-red-100"}`}>
                              {tx.amount > 0 ? (
                                <TrendingUp className="w-5 h-5 text-green-600" />
                              ) : (
                                <TrendingDown className="w-5 h-5 text-red-600" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-slate-700">{tx.reason}</p>
                              <p className="text-xs text-slate-400">
                                {new Date(tx.created_at).toLocaleDateString("es-ES", {
                                  day: "numeric",
                                  month: "short",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit"
                                })}
                              </p>
                            </div>
                          </div>
                          <span className={`text-lg font-bold ${tx.amount > 0 ? "text-green-600" : "text-red-500"}`}>
                            {tx.amount > 0 ? "+" : ""}{tx.amount}
                          </span>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ProfilePage;
